import { useIntl } from "react-intl";

import { Stack } from "@certa/catalyst/layouts/Stack";
import {
  Button,
  ButtonSizes,
  ButtonVariants
} from "@certa/catalyst/components/Button";

import { PlusLight } from "@certa/icons/components/PlusLight";

import type { ObjectWorkflowCreationType } from "./StartWorkflowFromObjectDialog.types";
import type { DataObjectWorkflowRelationItem } from "@certa/queries/types/dataObjectsAPIs.types";

type ObjectTypeHierarchyNavigatorProps = {
  objectTypeName: string;
  hasSelectedObjectInstance: boolean;
  shouldHideCreateObjectButton: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
  onCreateObjectWorkflow: (type: ObjectWorkflowCreationType) => void;
  currentObjectTypeWorkflowRelation: DataObjectWorkflowRelationItem | undefined;
  shouldShowRedirectLinkAndText: boolean;
};

export const ObjectTypeHierarchyNavigator = (
  props: ObjectTypeHierarchyNavigatorProps
) => {
  const {
    objectTypeName,
    hasSelectedObjectInstance,
    shouldHideCreateObjectButton = false,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    onCancel,
    onCreateObjectWorkflow,
    currentObjectTypeWorkflowRelation,
    shouldShowRedirectLinkAndText
  } = props;

  const intl = useIntl();

  const cancelLabel = intl.formatMessage({
    id: "comments.cancelButtonText",
    defaultMessage: "Cancel"
  });

  const nextLabel = intl.formatMessage({
    id: "commonTextInstances.fyiNextStep",
    defaultMessage: "Next"
  });

  const previousLabel = intl.formatMessage({
    id: "processWizard.previous",
    defaultMessage: "Previous"
  });

  const startLabel = intl.formatMessage({
    id: "commonTextInstances.start",
    defaultMessage: "Start"
  });

  const redirectLinkText = currentObjectTypeWorkflowRelation?.redirectLinkText;
  const redirectLinkUrl = currentObjectTypeWorkflowRelation?.redirectLinkUrl;

  return (
    <Stack
      direction="horizontal"
      align="center"
      justify="space-between"
      gap="8"
    >
      <Stack>
        {!shouldHideCreateObjectButton && (
          <Button
            variant={ButtonVariants.OUTLINE}
            leftIcon={<PlusLight />}
            size={ButtonSizes.SMALL}
            onClick={() => onCreateObjectWorkflow("createObject")}
          >
            {objectTypeName}
          </Button>
        )}
        {shouldShowRedirectLinkAndText &&
          redirectLinkText &&
          redirectLinkUrl && (
            <Button
              variant={ButtonVariants.LINK}
              href={redirectLinkUrl}
              target="_blank"
            >
              {redirectLinkText}
            </Button>
          )}
      </Stack>
      <Stack direction="horizontal" justify="flex-end" gap="8">
        {hasPrevious ? (
          <Button variant={ButtonVariants.OUTLINE} onClick={onPrevious}>
            {previousLabel}
          </Button>
        ) : (
          <Button variant={ButtonVariants.OUTLINE} onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        {!hasNext ? (
          <Button
            variant={ButtonVariants.FILLED}
            disabled={!hasSelectedObjectInstance}
            onClick={() => onCreateObjectWorkflow("initializeObject")}
          >
            {startLabel}
          </Button>
        ) : (
          <Button
            variant={ButtonVariants.FILLED}
            onClick={onNext}
            disabled={!hasNext || !hasSelectedObjectInstance}
          >
            {nextLabel}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
